import { type RefObject, useEffect, useRef } from "react";

interface UsePanelOutsideCloseOptions {
	readonly isOpen: boolean;
	readonly anchorRef: RefObject<HTMLElement | null>;
	readonly panelTestId: string;
	readonly onClose: () => void;
}

/**
 * Closes a filter panel when the user pointer-downs anywhere outside the
 * anchor chip and the (portaled) panel — Figma 4597:58865/4643:88785: an open
 * filter dropdown closes when the user clicks a different filter or a
 * pharmacy. Unlike `useOutsideDismiss` the outside click is NOT swallowed, so
 * the element the user tapped (another chip, a pharmacy card) still receives
 * it. Capture phase, because the picker chips stop pointerdown propagation.
 */
export function usePanelOutsideClose({
	isOpen,
	anchorRef,
	panelTestId,
	onClose,
}: UsePanelOutsideCloseOptions): void {
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target;
			if (!(target instanceof Element)) {
				return;
			}
			if (anchorRef.current?.contains(target)) {
				return;
			}
			if (target.closest(`[data-testid="${panelTestId}"]`)) {
				return;
			}
			onCloseRef.current();
		};
		document.addEventListener("pointerdown", handlePointerDown, true);
		return () => {
			document.removeEventListener("pointerdown", handlePointerDown, true);
		};
	}, [isOpen, anchorRef, panelTestId]);
}
